// src/components/station/StationPositionsInline.jsx
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Switch, IconButton, Tooltip
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import axios from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';
import { useSnackbar } from 'notistack';

export default function StationPositionsInline({ stationId }) {
  const { enqueueSnackbar } = useSnackbar();
  const { permissions } = useContext(UserContext);
  const canWrite = permissions.includes('stations.write');

  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/station-positions/station/${stationId}`);
      setPositions(res.data?.positions || []);
    } catch (e) {
      console.error(e);
      setPositions([]);
    } finally {
      setLoading(false);
    }
  }, [stationId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setRotateShifts = async (positionId, value) => {
    try {
      const params = value === null ? '' : `?rotate_shifts=${String(value)}`;
      await axios.put(`/station-positions/station/${stationId}/position/${positionId}/rotate-shifts${params}`);
      await fetchData();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo actualizar la rotación del área', { variant: 'error' });
    }
  };

  if (loading) {
    return <Typography variant="body2" sx={{ p: 1 }}>Cargando áreas…</Typography>;
  }

  if (!positions.length) {
    return <Typography variant="body2" sx={{ p: 1 }}>Esta estación todavía no tiene personal asignado a sus áreas.</Typography>;
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Rotación por área (sobrescribe la rotación general de la estación)
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Área</TableCell>
            <TableCell>Rotación efectiva</TableCell>
            <TableCell align="center">Rota</TableCell>
            <TableCell align="center">Override propio</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {positions.map((pos) => (
            <TableRow key={pos.id}>
              <TableCell>{pos.name}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={pos.effective_rotate_shifts ? 'Rota' : 'Fijo'}
                  color={pos.effective_rotate_shifts ? 'success' : 'default'}
                  variant={pos.has_override ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell align="center">
                <Switch
                  size="small"
                  checked={!!pos.effective_rotate_shifts}
                  disabled={!canWrite}
                  onChange={(e) => setRotateShifts(pos.id, e.target.checked)}
                />
              </TableCell>
              <TableCell align="center">
                {pos.has_override ? (
                  <Tooltip title="Heredar de la estación">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!canWrite}
                        onClick={() => setRotateShifts(pos.id, null)}
                      >
                        <RestartAltIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Typography variant="caption" color="text.secondary">Hereda de estación</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
